import { cn } from "@/lib/utils";
import "@assets/css/bootstrap-grid.css";
import { EyeClosedIcon } from "@assets/icons";
import { Button } from "@components/ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { ErrorLabel } from "@ui/internal/components/label";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { FormField } from "@utils/interface/form.interface";
import { ProfileFormData, ProfileSchema } from "@utils/interface/form.schema";
import { usePostData } from "@utils/query/index.query";
import { useSelectionStore, useUser } from "@utils/states/index.state";
import { Eye, LogOutIcon, TrashIcon } from "lucide-react";
import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router";

const ProfileForm: React.FC = () => {
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<ProfileFormData>({
    mode: "onChange",
    resolver: zodResolver(ProfileSchema),
  });
  const { user, setUser } = useUser();
  const { setIsFormPending } = useSelectionStore();
  /* set default value */
  const [userProfilePicture, setUserProfilePicture] = useState<string | File>(
    ""
  );
  const { mutate: updateProfile, isPending } = usePostData();

  const uploadToCloud = (file: File) => {
    updateProfile(
      {
        data: {
          image: file,
        },
        endpoint: endpoint["uploadimage"],
        key: "uploadFile",
      },
      {
        onSuccess: ({ data }) => {
          console.log("DAta", data);
          setValue("professional_image", data.image);
          clearErrors("professional_image");
          setUserProfilePicture(data.image);
        },
      }
    );
  };
  useEffect(() => {
    reset({
      first_name: user?.first_name,
      last_name: user?.last_name,
      email: user?.email,
      professional_image: user?.professional_image,
    });
    setUserProfilePicture(user?.professional_image);
  }, [user]);

  console.log("errors", user);

  const { mutate } = usePostData();
  const handleMutate = (data: any) => {
    setIsFormPending(true);
    data = {
      ...data,
      id: user?.id,
    };

    if (data.current_password || data.new_password || data.confirm_password) {
      return {
        data: {
          current_password: data.current_password,
          new_password: data.new_password,
        },
        endpoint: endpoint["change-password"],
        method: METHODS.POST,
      };
    }
    if (data?.professional_image) {
      setUser({
        ...user, // keep all existing fields
        first_name: data.first_name,
        last_name: data.last_name,
        professional_image: data.professional_image, // update only image
      });
    }
    return {
      data,
      endpoint: endpoint["user"],
      method: METHODS.PATCH,
    };
  };
  console.log("User", user);

  const handleSuccess = async ({
    status,
    data,
  }: {
    data: any;
    status: number;
  }) => {
    if ([200, 201].includes(status)) {
      setIsFormPending(false);
      // setUser(data);
      showToast({
        description: `Profile Updated Succesfully`,
        type: "success",
        title: "Success",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const handleError = async (error: any) => {
    setIsFormPending(false);
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    const errorMessage = error.data.response.data;

    throwErrorAlert(errorMessage);
  };

  const onSubmit: SubmitHandler<ProfileFormData> = (data) => {
    if (data.professional_image) {
      clearErrors("professional_image");
    }
    data = {
      ...data,
    };
    console.log("data", data);

    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };
  const [showPassword, setShowPassword] = useState({
    current_password: false,
    password: false,
    confirm_password: false,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const formSchema: FormField[] = [
    {
      type: "component",
      colSpan: 12,
      component: (
        <>
          <p className="text-[12px] mt-4 ">Profile Picture</p>
          <div className="flex items-center justify-between max-md:flex-col gap-4 max-md:items-start my-4">
            {" "}
            <div>
              {" "}
              <div className="flex items-center gap-2">
                <img
                  src={
                    userProfilePicture
                      ? userProfilePicture instanceof File
                        ? URL.createObjectURL(userProfilePicture)
                        : userProfilePicture
                      : "/user.png"
                  }
                  onClick={() => {
                    document.getElementById("userFile")?.click();
                  }}
                  alt="user-professional_image"
                  className={cn(
                    " ms-4 rounded-full cursor-pointer object-coverp-1 size-[50px]",
                    `${
                      errors?.professional_image && "border border-red-500 p-1"
                    }`
                  )}
                />
                <div className="flex flex-col">
                  {" "}
                  <span className="font-semibold text-[#202020]">
                    Change Profile Picture
                  </span>
                  <span className="text-[14px] text-[#60595E]">
                    PNG, JPG, JPEG (MAX. 15MB)
                  </span>
                </div>{" "}
              </div>
              <ErrorLabel
                className="ps-4"
                message={errors?.professional_image?.message}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  document.getElementById("userFile")?.click();
                }}
                type="button"
                disabled={isPending}
                variant={"outline"}
              >
                {isPending ? "Uploading..." : "Upload new photo"}
              </Button>
              <input
                type="file"
                onChange={(e) => {
                  if (e.target.files) {
                    uploadToCloud(e.target.files[0]);
                    clearErrors("professional_image");
                  }
                }}
                accept="image/*"
                id="userFile"
                className="hidden"
              />
              <Button
                onClick={() => {
                  setUserProfilePicture("");
                  setValue("professional_image", "");
                  clearErrors("professional_image");
                }}
                type="button"
                variant={"destructive"}
              >
                <TrashIcon />
                Delete photo
              </Button>
            </div>
          </div>
        </>
      ),
      visable: searchParams.get("type") == "details" ? true : false,
    },
    {
      type: "component",
      colSpan: 12,
      component: <p className="text-[12px] mt-4 ">Full Name</p>,
      visable: searchParams.get("type") == "details" ? true : false,
    },
    {
      name: "first_name",
      label: "First Name",
      colSpan: 6,
      type: "text",
      placeholder: "Aayush",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: searchParams.get("type") == "details" ? true : false,
    },
    {
      name: "last_name",
      label: "Last Name",
      colSpan: 6,
      type: "text",
      placeholder: "Maharjan",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: searchParams.get("type") == "details" ? true : false,
    },
    {
      type: "component",
      colSpan: 12,
      component: (
        <div className="my-4">
          <p className="text-[12px] ">Contact Email</p>
          <p className="text-[16px] font-semibold ">
            Update and manage your email address here.
          </p>
        </div>
      ),
      visable: searchParams.get("type") == "details" ? true : false,
    },
    {
      name: "email",
      label: "Email",
      colSpan: 6,
      type: "mail",
      placeholder: "aayush@gmail.com",
      disabled: true,
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: searchParams.get("type") == "details" ? true : false,
    },
    {
      type: "component",
      colSpan: 12,
      component: <hr />,
    },
    {
      type: "component",
      colSpan: 12,
      component: (
        <div className="my-4">
          <p className="text-[12px] ">Password</p>
          <p className="text-[16px] font-semibold ">
            Modify your account password here.
          </p>
        </div>
      ),
      visable: searchParams.get("type") == "details" ? false : true,
    },
    {
      name: "current_password",
      label: "Current Password",
      colSpan: 6,
      type: `${showPassword.current_password ? "text" : "password"}`,
      placeholder: "**********",
      control: control,
      setValue: setValue,
      errors,
      endIcon: (
        <>
          {showPassword.current_password ? (
            <Eye
              size={17}
              color="#ABABAB"
              onClick={() =>
                setShowPassword((prevState) => ({
                  ...prevState,
                  current_password: !prevState.current_password,
                }))
              }
            />
          ) : (
            <span
              onClick={() =>
                setShowPassword((prevState) => ({
                  ...prevState,
                  current_password: !prevState.current_password,
                }))
              }
            >
              <EyeClosedIcon />
            </span>
          )}
        </>
      ),
      className: `mb-[20px] min-w-full w-full`,
      visable: searchParams.get("type") == "details" ? false : true,
    },
    {
      name: "new_password",
      label: "Password ",
      colSpan: 6,
      type: `${showPassword.password ? "text" : "password"}`,
      placeholder: "***************",
      control: control,
      endIcon: (
        <>
          {showPassword.password ? (
            <Eye
              size={17}
              color="#ABABAB"
              onClick={() =>
                setShowPassword((prevState) => ({
                  ...prevState,
                  password: !prevState.password,
                }))
              }
            />
          ) : (
            <span
              onClick={() =>
                setShowPassword((prevState) => ({
                  ...prevState,
                  password: !prevState.password,
                }))
              }
            >
              <EyeClosedIcon />
            </span>
          )}
        </>
      ),
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: searchParams.get("type") == "details" ? false : true,
    },
    {
      name: "confirm_password",
      label: "Confirm Password ",
      colSpan: 6,
      type: `${showPassword.confirm_password ? "text" : "password"}`,
      placeholder: "***************",
      control: control,
      endIcon: (
        <>
          {showPassword.confirm_password ? (
            <Eye
              size={17}
              color="#ABABAB"
              onClick={() =>
                setShowPassword((prevState) => ({
                  ...prevState,
                  confirm_password: !prevState.confirm_password,
                }))
              }
            />
          ) : (
            <span
              onClick={() =>
                setShowPassword((prevState) => ({
                  ...prevState,
                  confirm_password: !prevState.confirm_password,
                }))
              }
            >
              <EyeClosedIcon />
            </span>
          )}
        </>
      ),
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: searchParams.get("type") == "details" ? false : true,
    },
    {
      type: "component",
      colSpan: 12,
      component: <hr />,
    },
    // {
    //   type: "component",
    //   colSpan: 12,
    //   component: (
    //     <div className="my-4">
    //       <p className="text-[12px] ">Account Security</p>
    //       <p className="text-[16px] font-semibold ">
    //         Manage your account security here.
    //       </p>
    //     </div>
    //   ),
    // },
    // {
    //   type: "component",
    //   colSpan: 12,
    //   component: (
    //     <Button>
    //       {" "}
    //       <LogOutIcon />
    //       Logout
    //     </Button>
    //   ),
    // },
  ];

  return (
    <>
      <form
        id="profile"
        onSubmit={handleSubmit(onSubmit)}
        className="row w-full"
        autoComplete="off"
      >
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`rofcol-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};
export default ProfileForm;
